import { Plus, Trash2 } from 'lucide-react';
import { ProductPicker } from './ProductPicker';
import type {
    VisitReportDraft,
    VisitLocation,
    CustomerAttendee,
    ProductDiscussed,
    NextStep,
} from '../../types/visitReport';

interface DraftReviewProps {
    draft: VisitReportDraft;
    onChange: (patch: Partial<VisitReportDraft>) => void;
    companyName?: string | null;
    companyCode?: string;
}

const LOCATION_OPTIONS: Array<{ value: VisitLocation; label: string }> = [
    { value: 'customer-site', label: 'Customer site' },
    { value: 'dolder-office', label: 'Dolder office' },
    { value: 'trade-fair', label: 'Trade fair' },
    { value: 'remote', label: 'Remote (video call)' },
    { value: 'other', label: 'Other' },
];

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
    return (
        <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{label}</span>
            {children}
            {hint && <span className="text-xs text-gray-400">{hint}</span>}
        </label>
    );
}

export function DraftReview({ draft, onChange, companyName, companyCode }: DraftReviewProps) {
    const updateArray = <T,>(key: keyof VisitReportDraft, arr: T[]) => {
        onChange({ [key]: arr } as Partial<VisitReportDraft>);
    };

    const attendees = draft.customerAttendees ?? [];
    const dolderAttendees = draft.dolderAttendees ?? [];
    const topics = draft.topicsDiscussed ?? [];
    const products = draft.productsDiscussed ?? [];
    const nextSteps = draft.nextSteps ?? [];

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Visit date">
                    <input
                        type="date"
                        value={draft.visitDate ?? ''}
                        onChange={e => onChange({ visitDate: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </Field>
                <Field label="Duration (min)">
                    <input
                        type="number"
                        min={0}
                        value={draft.durationMinutes ?? ''}
                        onChange={e => onChange({ durationMinutes: e.target.value ? Number(e.target.value) : undefined })}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </Field>
                <Field label="Location type">
                    <select
                        value={draft.location ?? ''}
                        onChange={e => onChange({ location: (e.target.value || undefined) as VisitLocation | undefined })}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">— choose —</option>
                        {LOCATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </Field>
                <Field label="Location detail" hint="City / event name / room">
                    <input
                        type="text"
                        value={draft.locationDetail ?? ''}
                        onChange={e => onChange({ locationDetail: e.target.value })}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </Field>
            </div>

            <Field label="Purpose" hint="One-line summary of why the visit took place">
                <input
                    type="text"
                    value={draft.purpose ?? ''}
                    onChange={e => onChange({ purpose: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </Field>

            {/* Customer attendees */}
            <div>
                <div className="flex items-start justify-between gap-2 mb-2">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide break-words min-w-0 flex-1">
                        Customer attendees{companyName ? ` — ${companyName}` : ''}
                    </span>
                    <button
                        type="button"
                        onClick={() => updateArray<CustomerAttendee>('customerAttendees', [...attendees, { name: '' }])}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 flex-shrink-0"
                    >
                        <Plus className="w-3 h-3" /> Add
                    </button>
                </div>
                <div className="space-y-2">
                    {attendees.length === 0 && <div className="text-sm text-gray-400 italic">No customer attendees yet.</div>}
                    {attendees.map((a, i) => (
                        <div key={i} className="flex gap-2 items-center">
                            <input
                                type="text"
                                placeholder="Name"
                                value={a.name}
                                onChange={e => {
                                    const copy = [...attendees];
                                    copy[i] = { ...copy[i], name: e.target.value };
                                    updateArray('customerAttendees', copy);
                                }}
                                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <input
                                type="text"
                                placeholder="Role"
                                value={a.role ?? ''}
                                onChange={e => {
                                    const copy = [...attendees];
                                    copy[i] = { ...copy[i], role: e.target.value };
                                    updateArray('customerAttendees', copy);
                                }}
                                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <button
                                type="button"
                                onClick={() => updateArray('customerAttendees', attendees.filter((_, j) => j !== i))}
                                className="p-2 text-gray-400 hover:text-red-600"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Dolder attendees */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Dolder attendees</span>
                    <button
                        type="button"
                        onClick={() => updateArray<string>('dolderAttendees', [...dolderAttendees, ''])}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                        <Plus className="w-3 h-3" /> Add
                    </button>
                </div>
                <div className="space-y-2">
                    {dolderAttendees.map((name, i) => (
                        <div key={i} className="flex gap-2 items-center">
                            <input
                                type="text"
                                value={name}
                                onChange={e => {
                                    const copy = [...dolderAttendees];
                                    copy[i] = e.target.value;
                                    updateArray('dolderAttendees', copy);
                                }}
                                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <button
                                type="button"
                                onClick={() => updateArray('dolderAttendees', dolderAttendees.filter((_, j) => j !== i))}
                                className="p-2 text-gray-400 hover:text-red-600"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Topics */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Topics discussed</span>
                    <button
                        type="button"
                        onClick={() => updateArray<string>('topicsDiscussed', [...topics, ''])}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                        <Plus className="w-3 h-3" /> Add
                    </button>
                </div>
                <div className="space-y-2">
                    {topics.map((t, i) => (
                        <div key={i} className="flex gap-2 items-center">
                            <input
                                type="text"
                                value={t}
                                onChange={e => {
                                    const copy = [...topics];
                                    copy[i] = e.target.value;
                                    updateArray('topicsDiscussed', copy);
                                }}
                                className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <button
                                type="button"
                                onClick={() => updateArray('topicsDiscussed', topics.filter((_, j) => j !== i))}
                                className="p-2 text-gray-400 hover:text-red-600"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Products */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Products discussed</span>
                    <button
                        type="button"
                        onClick={() => updateArray<ProductDiscussed>('productsDiscussed', [...products, { productName: '' }])}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                        <Plus className="w-3 h-3" /> Add
                    </button>
                </div>
                <div className="space-y-2">
                    {products.map((p, i) => (
                        <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-start">
                            <input
                                type="text"
                                placeholder="Product name"
                                value={p.productName}
                                onChange={e => {
                                    const copy = [...products];
                                    copy[i] = { ...copy[i], productName: e.target.value };
                                    updateArray('productsDiscussed', copy);
                                }}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <ProductPicker
                                companyCode={companyCode}
                                productCode={p.productCode}
                                productName={p.productName}
                                placeholder="Pick product code …"
                                onPick={match => {
                                    const copy = [...products];
                                    if (match) {
                                        copy[i] = {
                                            ...copy[i],
                                            productCode: match.productCode,
                                            // Only overwrite name when it's empty or was a fuzzy
                                            // AI guess — preserve user-typed names.
                                            productName: copy[i].productName?.trim()
                                                ? copy[i].productName
                                                : match.productName,
                                        };
                                        // If the current name looks like an AI extract that exactly
                                        // equals the canonical name (case-insensitive), normalise it.
                                        if (copy[i].productName?.toUpperCase() === match.productName.toUpperCase()) {
                                            copy[i].productName = match.productName;
                                        }
                                    } else {
                                        copy[i] = { ...copy[i], productCode: undefined };
                                    }
                                    updateArray('productsDiscussed', copy);
                                }}
                            />
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="Notes"
                                    value={p.notes ?? ''}
                                    onChange={e => {
                                        const copy = [...products];
                                        copy[i] = { ...copy[i], notes: e.target.value };
                                        updateArray('productsDiscussed', copy);
                                    }}
                                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                                <button
                                    type="button"
                                    onClick={() => updateArray('productsDiscussed', products.filter((_, j) => j !== i))}
                                    className="p-2 text-gray-400 hover:text-red-600"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <Field label="Pricing / volume">
                <textarea
                    rows={2}
                    value={draft.pricingVolume ?? ''}
                    onChange={e => onChange({ pricingVolume: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
            </Field>

            <Field label="Competitor mentions">
                <textarea
                    rows={2}
                    value={draft.competitorMentions ?? ''}
                    onChange={e => onChange({ competitorMentions: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
            </Field>

            <Field label="Customer pain points">
                <textarea
                    rows={2}
                    value={draft.customerPainPoints ?? ''}
                    onChange={e => onChange({ customerPainPoints: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
            </Field>

            <Field label="Opportunities">
                <textarea
                    rows={2}
                    value={draft.opportunities ?? ''}
                    onChange={e => onChange({ opportunities: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
            </Field>

            {/* Next steps */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Next steps</span>
                    <button
                        type="button"
                        onClick={() => updateArray<NextStep>('nextSteps', [...nextSteps, { action: '' }])}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                        <Plus className="w-3 h-3" /> Add
                    </button>
                </div>
                <div className="space-y-2">
                    {nextSteps.map((s, i) => (
                        <div key={i} className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto] gap-2 items-start">
                            <input
                                type="text"
                                placeholder="Action"
                                value={s.action}
                                onChange={e => {
                                    const copy = [...nextSteps];
                                    copy[i] = { ...copy[i], action: e.target.value };
                                    updateArray('nextSteps', copy);
                                }}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <input
                                type="text"
                                placeholder="Owner"
                                value={s.owner ?? ''}
                                onChange={e => {
                                    const copy = [...nextSteps];
                                    copy[i] = { ...copy[i], owner: e.target.value };
                                    updateArray('nextSteps', copy);
                                }}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <input
                                type="date"
                                value={s.dueDate ?? ''}
                                onChange={e => {
                                    const copy = [...nextSteps];
                                    copy[i] = { ...copy[i], dueDate: e.target.value };
                                    updateArray('nextSteps', copy);
                                }}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <button
                                type="button"
                                onClick={() => updateArray('nextSteps', nextSteps.filter((_, j) => j !== i))}
                                className="p-2 text-gray-400 hover:text-red-600 self-center"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Follow-up meeting date">
                    <input
                        type="date"
                        value={draft.followUpMeeting?.plannedDate ?? ''}
                        onChange={e => onChange({
                            followUpMeeting: { ...(draft.followUpMeeting || {}), plannedDate: e.target.value },
                        })}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                </Field>
                <Field label="Follow-up topic">
                    <input
                        type="text"
                        value={draft.followUpMeeting?.topic ?? ''}
                        onChange={e => onChange({
                            followUpMeeting: { ...(draft.followUpMeeting || {}), topic: e.target.value },
                        })}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                </Field>
            </div>
        </div>
    );
}
